import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PublicLayout } from '@/components/public';
import NoticeDeadlineWidget from '@/components/marketing/NoticeDeadlineWidget';
import { t } from '@/i18n';
import { FIELD_APP_URL, fieldAppUrl, FIELD_COMMAND_PUBLIC } from '@/utils/fieldApp';
import { 
  FileText, Calculator, BookOpen, Bell, Cloud, FileCheck, 
  Mail, BarChart3, Plug, MapPin, CheckCircle, ArrowRight, Gift,
  Clock, Shield, Users, Zap, Award, ClipboardList, DollarSign, ShieldCheck, Smartphone, Briefcase
} from 'lucide-react';


const US_STATES = [
  { code: 'FL', name: t("Florida") }, { code: 'CA', name: t("California") }, { code: 'GA', name: t("Georgia") },
  { code: 'TX', name: t("Texas") }, { code: 'AZ', name: t("Arizona") }, { code: 'NV', name: t("Nevada") },
  { code: 'WA', name: t("Washington") }, { code: 'OR', name: t("Oregon") }, { code: 'CO', name: t("Colorado") },
  { code: 'IL', name: t("Illinois") }, { code: 'NC', name: t("North Carolina") }, { code: 'PA', name: t("Pennsylvania") },
  { code: 'NY', name: t("New York") }, { code: 'AL', name: t("Alabama") }, { code: 'AK', name: t("Alaska") },
  { code: 'AR', name: t("Arkansas") }, { code: 'CT', name: t("Connecticut") }, { code: 'DE', name: t("Delaware") },
  { code: 'HI', name: t("Hawaii") }, { code: 'ID', name: t("Idaho") },
];

const FEATURES = [
  { icon: Bell, title: t("Intelligent Platform"), desc: t("Monitor all project deadlines automatically. Never miss another due date with smart tracking and alerts.") },
  { icon: Calculator, title: t("Deadline Calculator"), desc: t("Calculate your preliminary notice, mechanic's lien and bond claim deadlines in just 3 simple steps.") },
  { icon: BookOpen, title: t("50 States Learning Center"), desc: t("Preliminary notice, lien and bond laws resources in every state to keep you informed.") },
  { icon: Clock, title: t("Track Every Deadline"), desc: t("Real-time tracking of all your notices and lien deadlines with automatic reminders.") },
  { icon: Cloud, title: t("Online File Storage"), desc: t("Secure cloud storage for all your notices, documents, and proof of service records.") },
  { icon: FileCheck, title: t("Free Waivers & Releases"), desc: t("Generate lien waivers and releases at no additional cost. Streamline your payment process.") },
  { icon: Mail, title: t("Live USPS Tracking"), desc: t("Real-time certified mail tracking with delivery confirmation and proof of service.") },
  { icon: BarChart3, title: t("Custom Reports"), desc: t("Generate detailed reports on all your notices, projects, and billing in seconds.") },
  { icon: Plug, title: t("API Integration"), desc: t("Seamlessly integrate with your existing software systems via our robust API.") },
];

const FIELD_COMMAND_POINTS = [
  { icon: ClipboardList, title: t("Daily logs in 60 seconds"), desc: t("Weather, crew, hours and photos — logged from the truck before you drive off.") },
  { icon: DollarSign, title: t("Know who owes you what"), desc: t("Track invoices, payments and retainage per job, with a running balance you can act on.") },
  { icon: ShieldCheck, title: t("Lien rights handled for you"), desc: t("Every new jobsite gets its notice deadline calculated and the paperwork filed through All American Notice.") },
];

const HomePage = () => {
  const navigate = useNavigate();

  // FAQ Schema for homepage SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "@id": "https://allamericannotice.com/#faq-1",
        "name": "What is a preliminary notice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t("A preliminary notice is a document sent to property owners and other parties to preserve your right to file a mechanics lien. It notifies them that you are working on or supplying materials to their project and protects your ability to get paid.")
        }
      },
      {
        "@type": "Question",
        "@id": "https://allamericannotice.com/#faq-2",
        "name": "Do I need to send a preliminary notice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t("In most states, yes. Preliminary notices are required to preserve your lien rights. Even in states where they're not mandatory, sending one significantly improves your chances of getting paid and can speed up the payment process.")
        }
      },
      {
        "@type": "Question",
        "@id": "https://allamericannotice.com/#faq-3",
        "name": "What is the deadline for sending a preliminary notice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": t("Deadlines vary by state, typically ranging from 20 to 45 days from when you first provide labor or materials. Missing the deadline can result in losing your lien rights entirely.")
        }
      }
    ]
  };

  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 lg:py-24 bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f]" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid-white/[0.02] opacity-40"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <p className="text-[#F97316] font-semibold mb-4 text-sm lg:text-base uppercase tracking-wide">{t("Trusted by 1000s of Construction Companies")}</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white tracking-tight leading-tight mb-6">
                {t("Run the Job.")}
                <span className="text-[#F97316]"> {t("Protect the Money.")}</span>
              </h1>
              <p className="text-base lg:text-lg text-slate-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {FIELD_COMMAND_PUBLIC
                  ? t("One platform for construction project management and lien protection — daily logs, change orders and payments on the jobsite, with preliminary notices, waivers and liens filed in all 50 states.")
                  : t("Construction document services: Florida Notices of Commencement, preliminary notices, waivers, mechanic's liens and bond claims — prepared, filed and tracked in all 50 states. Pay per document, no monthly fee.")}
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-sm text-slate-300">                <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> {t("No monthly fees")}</span>
                <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> {t("Notices from $35 + certified mail postage")}</span>
                {FIELD_COMMAND_PUBLIC && (
                  <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> {t("Field Command free for 1 project")}</span>
                )}
              </div>
            </div>
            <div className="relative" data-testid="hero-noc-card">
              <div className="absolute -inset-4 bg-[#F97316]/25 rounded-3xl blur-3xl"></div>
              <div className="relative rounded-2xl bg-white shadow-2xl border-4 border-[#F97316] p-6 sm:p-8">
                <span className="inline-block bg-[#F97316] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">{t("Florida · Now fully online")}</span>
                <h2 className="mt-4 text-2xl sm:text-3xl font-black text-[#0F172A] leading-tight">{t("Florida Notice of Commencement")}</h2>
                <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
                  {t("We research the property, the owner approves and signs online with our notary, and it is recorded with the county. No notary visit, no trip to the clerk.")}
                </p>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {[t("Owner of record, folio and legal description pulled for you"), t("Online notarization (RON) included"), t("County first-page recording fee included")].map((pt) => (
                    <li key={pt} className="flex gap-2"><CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> {pt}</li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                  <Button onClick={() => navigate('/florida-noc')} size="lg"
                          className="bg-[#F97316] hover:bg-[#ea6a10] text-white font-bold h-12 px-6 shadow-lg shadow-orange-500/25 transition-transform hover:-translate-y-0.5"
                          data-testid="hero-noc-cta">
                    {t("Start my Florida NOC")} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <span className="text-sm text-slate-500"><strong className="text-[#0F172A] text-lg">$135</strong> {t("· $115 each at 25+ a month")}</span>
                </div>
                <p className="mt-3 text-sm text-emerald-700 font-semibold" data-testid="hero-noc-deals">
                  {t("Skip the online notary and save $25 · Prepay 25+ NOCs at $115 each, good for 90 days")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Choose your platform — the whole chooser is a two-sided framing, so it goes as a unit
          while Field Command is hidden rather than leaving one lonely card under
          "Where do you want to start?" */}
      {FIELD_COMMAND_PUBLIC && (
      <section className="py-12 lg:py-16 bg-white border-b border-slate-200" data-testid="platform-chooser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-10">
            <p className="text-[#F97316] font-semibold mb-2 uppercase tracking-wide text-sm">{t("One company, two sides of the job")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f]">{t("Where do you want to start?")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <Card
              className="border-2 border-[#F97316]/40 hover:border-[#F97316] hover:shadow-xl transition-all cursor-pointer group bg-white relative overflow-hidden"
              onClick={() => { if (FIELD_APP_URL) { window.open(FIELD_APP_URL, '_blank', 'noopener'); } else { navigate('/register'); } }}
              data-testid="chooser-card-field"
            >
              <span className="absolute top-0 right-0 bg-[#F97316] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-lg">{t("New")}</span>
              <CardContent className="p-7 lg:p-9">
                <div className="w-14 h-14 rounded-xl bg-[#F97316]/10 flex items-center justify-center mb-5 group-hover:bg-[#F97316] transition-colors">
                  <Smartphone className="h-7 w-7 text-[#F97316] group-hover:text-white transition-colors" />
                </div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{t("Project Management")}</p>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1e3a5f]">{t("Field Command")}</h3>
                <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                  {t("Daily logs, photos, time clock, change orders and payments from your phone — with lien deadlines handled for you. Free for 1 project, then from $45/mo.")}
                </p>
                <p className="mt-5 text-[#F97316] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  {t("Start free — no card, no trial clock")} <ArrowRight className="h-4 w-4" />
                </p>
              </CardContent>
            </Card>
            <Card
              className="border-2 border-slate-200 hover:border-[#1e3a5f] hover:shadow-xl transition-all cursor-pointer group bg-white"
              onClick={() => navigate('/pricing')}
              data-testid="chooser-card-notices"
            >
              <CardContent className="p-7 lg:p-9">
                <div className="w-14 h-14 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mb-5 group-hover:bg-[#1e3a5f] transition-colors">
                  <Shield className="h-7 w-7 text-[#1e3a5f] group-hover:text-white transition-colors" />
                </div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{t("Lien Protection")}</p>
                <h3 className="text-xl lg:text-2xl font-bold text-[#1e3a5f]">{t("All American Notice")}</h3>
                <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                  {t("Preliminary notices, mechanic's liens, waivers and bond claims — prepared, mailed and tracked in all 50 states. Pay per notice, no monthly fee.")}
                </p>
                <p className="mt-5 text-[#1e3a5f] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  {t("Notices from $35")} <ArrowRight className="h-4 w-4" />
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      )}

      {/* Field Command */}
      {FIELD_COMMAND_PUBLIC && (
      <section className="py-16 lg:py-20 bg-[#0f2440]" data-testid="field-command-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F97316] font-semibold mb-3 uppercase tracking-wide text-sm">{t("New — Field Command")}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                {t("Your jobsite, under control.")}
              </h2>
              <p className="text-slate-300 mt-4 text-base lg:text-lg leading-relaxed">
                {t("Daily logs, photos, time clock, change orders and payments from your phone — and the lien paperwork files itself through All American Notice.")}
              </p>
              <ul className="mt-8 space-y-4">
                {FIELD_COMMAND_POINTS.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-9 h-9 rounded-lg bg-[#F97316]/15 flex items-center justify-center shrink-0">
                      <point.icon className="h-4 w-4 text-[#F97316]" />
                    </span>
                    <div>
                      <p className="text-white font-semibold text-sm">{point.title}</p>
                      <p className="text-slate-400 text-sm">{point.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                {FIELD_APP_URL ? (
                  <Button
                    asChild
                    size="lg"
                    className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-base px-8 h-12 lg:h-14 font-semibold shadow-lg shadow-orange-500/25"
                    data-testid="field-command-cta"
                  >
                    <a href={FIELD_APP_URL} target="_blank" rel="noreferrer">
                      {t("Try Field Command")} <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    onClick={() => navigate('/register')}
                    className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-base px-8 h-12 lg:h-14 font-semibold shadow-lg shadow-orange-500/25"
                    data-testid="field-command-cta"
                  >
                    {t("Try Field Command")} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                )}
                {FIELD_APP_URL ? (
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-[#1e3a5f] text-base px-8 h-12 lg:h-14"
                    data-testid="field-command-pricing"
                  >
                    <a href={fieldAppUrl('/pricing')} target="_blank" rel="noreferrer">
                      {t("See Field Command Plans")}
                    </a>
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/pricing')}
                    className="bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-[#1e3a5f] text-base px-8 h-12 lg:h-14"
                    data-testid="field-command-pricing"
                  >
                    {t("See Field Command Plans")}
                  </Button>
                )}
              </div>
              <p className="text-slate-400 text-sm mt-5">
                {t("Free for 1 project · From $45/mo after that · One login for your whole crew")}
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#F97316]/20 rounded-2xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1621905253185-95614217f357?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBmb3JlbWFuJTIwcGhvbmUlMjBqb2JzaXRlJTIwdGFibGV0fGVufDB8fHx8MTc5MDk2MjkwN3ww&ixlib=rb-4.1.0&q=85&w=700"
                alt={t("Contractor updating a daily log from the jobsite")}
                loading="lazy"
                className="relative rounded-2xl shadow-2xl border-4 border-white/10 w-full object-cover max-h-[460px]"
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Pricing Banner */}
      <section className="bg-[#F97316] py-4 lg:py-5">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-white">
            <Gift className="h-6 w-6 flex-shrink-0" />
            <span className="font-bold text-base lg:text-lg">{t("VOLUME PRICING:")}</span>
            <span className="text-sm lg:text-base">{t("Notices from $35 + certified mail postage.")}</span>
          </div>
        </div>
      </section>

      {/* Hiring banner */}
      <section className="bg-[#0F172A] py-4" data-testid="home-hiring-banner">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <Briefcase className="h-5 w-5 text-[#F97316] shrink-0" />
          <span className="text-white text-sm lg:text-base">
            <strong>{t("We're hiring sales representatives.")}</strong> {t("Commission-based, work from anywhere in the United States.")}
          </span>
          <Link to="/careers" className="text-[#F97316] font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-hiring-link">
            {t("See the role")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Quick Action Cards */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <NoticeDeadlineWidget />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card 
              className="border-2 border-slate-200 hover:border-[#1e3a5f] hover:shadow-xl transition-all cursor-pointer group bg-white"
              onClick={() => navigate('/register')}
              data-testid="action-card-notice"
            >
              <CardContent className="p-6 lg:p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a5f] group-hover:scale-110 transition-all">
                  <FileText className="h-8 w-8 text-[#1e3a5f] group-hover:text-white transition-colors" />
                </div>
                <p className="text-sm text-slate-500 mb-1">{t("I want to")}</p>
                <h3 className="text-xl font-bold text-[#1e3a5f]">{t("Notices from $35")}</h3>
              </CardContent>
            </Card>
            <Card 
              className="border-2 border-slate-200 hover:border-[#1e3a5f] hover:shadow-xl transition-all cursor-pointer group bg-white"
              onClick={() => navigate('/notice-deadline-calculator')}
              data-testid="action-card-calculator"
            >
              <CardContent className="p-6 lg:p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a5f] group-hover:scale-110 transition-all">
                  <Calculator className="h-8 w-8 text-[#1e3a5f] group-hover:text-white transition-colors" />
                </div>
                <p className="text-sm text-slate-500 mb-1">{t("I want to")}</p>
                <h3 className="text-xl font-bold text-[#1e3a5f]">{t("Calculate Deadlines")}</h3>
              </CardContent>
            </Card>
            <Card 
              className="border-2 border-slate-200 hover:border-[#1e3a5f] hover:shadow-xl transition-all cursor-pointer group bg-white"
              onClick={() => navigate('/faq')}
              data-testid="action-card-learn"
            >
              <CardContent className="p-6 lg:p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#1e3a5f] group-hover:scale-110 transition-all">
                  <BookOpen className="h-8 w-8 text-[#1e3a5f] group-hover:text-white transition-colors" />
                </div>
                <p className="text-sm text-slate-500 mb-1">{t("I want to")}</p>
                <h3 className="text-xl font-bold text-[#1e3a5f]">{t("Learn Lien Laws")}</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-16 lg:py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F97316] font-semibold mb-2 uppercase tracking-wide text-sm">{t("Platform Features")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f]">{t("Our Platform is User Friendly")}</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-base lg:text-lg">
              {t("Full-service preliminary notice and lien management. Our highly trained professionals ensure your lien rights are secure.")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg hover:border-[#1e3a5f]/30 transition-all group">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center mb-3 group-hover:bg-[#1e3a5f] transition-colors">
                    <feature.icon className="h-6 w-6 text-[#1e3a5f] group-hover:text-white transition-colors" />
                  </div>
                  <CardTitle className="text-lg text-[#1e3a5f]">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <p className="text-4xl lg:text-5xl font-black text-[#F97316] mb-2">1M+</p>
              <p className="text-white text-base lg:text-lg">{t("Notices Sent")}</p>
            </div>
            <div className="p-6 border-y md:border-y-0 md:border-x border-white/20">
              <p className="text-4xl lg:text-5xl font-black text-[#F97316] mb-2">$10B+</p>
              <p className="text-white text-base lg:text-lg">{t("Money Secured")}</p>
            </div>
            <div className="p-6">
              <p className="text-4xl lg:text-5xl font-black text-[#F97316] mb-2">1000s</p>
              <p className="text-white text-base lg:text-lg">{t("Happy Customers")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 50 States Coverage */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="states-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f]">{t("Coverage Across All 50 States")}</h2>
            <p className="text-slate-600 mt-4">{t("State-specific forms and deadlines for preliminary notice requirements")}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 max-w-5xl mx-auto">
            {US_STATES.map((state) => (
              <Link
                key={state.code}
                to={`/learning-center/${state.code.toLowerCase()}`}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-[#1e3a5f] hover:text-white border border-slate-200 rounded-lg transition-colors cursor-pointer group"
                data-testid={`state-link-${state.code}`}
              >
                <span className="font-bold text-[#1e3a5f] group-hover:text-white">{state.code}</span>
                <span className="text-sm text-slate-600 group-hover:text-slate-200">{state.name}</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button 
              variant="outline" 
              className="gap-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
              onClick={() => navigate('/learning-center')}
              data-testid="view-all-states-btn"
            >
              <MapPin className="h-4 w-4" />
              {t("View All State Requirements")}
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-20 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f]">{t("Simple as 1-2-3")}</h2>
            <p className="text-slate-600 mt-4">{t("Create and send compliant preliminary notices in minutes")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { step: '1', title: t("Enter Project Details"), desc: t("Add your project information including property owner, general contractor, and job site details.") },
              { step: '2', title: t("Generate Notice"), desc: t("Select the state and our system automatically generates a compliant preliminary notice.") },
              { step: '3', title: t("Send & Track"), desc: t("Send via certified mail and track delivery status with proof of service in real-time.") },
            ].map((item, index) => (
              <div key={index} className="text-center relative">
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-slate-300"></div>
                )}
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: t("Secure & Compliant"), desc: t("State-compliant documents") },
              { icon: Zap, title: t("Fast Processing"), desc: t("Same-day service available") },
              { icon: Users, title: t("Expert Support"), desc: t("Dedicated specialists") },
              { icon: Award, title: t("Industry Trusted"), desc: t("Since 2020") },
            ].map((item, index) => (
              <div key={index} className="text-center p-4">
                <item.icon className="h-8 w-8 text-[#F97316] mx-auto mb-2" />
                <h4 className="font-semibold text-[#1e3a5f] text-sm">{item.title}</h4>
                <p className="text-slate-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-[#1e3a5f]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t("Manage Your Lien and Bond Claim Rights with Confidence")}
          </h2>
          <p className="text-slate-300 text-base lg:text-lg mb-8">
            {t("Avoid costly mistakes and never miss a deadline. Join thousands of construction professionals who trust our platform.")}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')} 
            className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-base lg:text-lg px-10 h-12 lg:h-14 font-semibold shadow-lg shadow-orange-500/25"
          >
            {t("Get Started Today")} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-slate-400 text-sm">
            <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> {t("No monthly fees")}</span>
            <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> {t("Only pay for completed work")}</span>
            <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-400" /> {t("No setup costs")}</span>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default HomePage;
